import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';

/** Only what a dispatcher can fire from the console. */
const LABELS: Record<string, string> = {
  triage: 'Triage',
  dispatch: 'Start dispatch',
  acknowledge: 'Mark acknowledged',
  start_travel: 'Start travel',
  arrive: 'Mark arrived',
  begin_work: 'Begin work',
  complete: 'Complete',
  verify: 'Verify',
  close: 'Close',
  hold: 'Put on hold',
  resume: 'Resume',
  cancel: 'Cancel',
  reopen: 'Reopen',
};

/** Events the machine will refuse without extra input. */
const NEEDS_REASON = new Set(['hold', 'cancel', 'reopen']);
const NEEDS_OUTCOME = new Set(['complete']);

const OUTCOMES = ['COMPLETED', 'NO_ACCESS', 'NO_FAULT_FOUND', 'DEFERRED'];
const HOLD_STATES = [
  ['ON_HOLD', 'On hold (weather, decision)'],
  ['PENDING_CUSTOMER', 'Waiting on the client'],
  ['PENDING_ACCESS', 'Waiting on access / wayleave'],
  ['PENDING_PARTS', 'Waiting on materials'],
];

export function ActionBar({ jobId, version }: { jobId: string; version: number }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [form, setForm] = useState({
    reason: '',
    outcome: 'COMPLETED',
    rootCauseCode: '',
    holdStatus: 'ON_HOLD',
  });

  const actions = useQuery({
    queryKey: ['actions', jobId],
    queryFn: () => api.ticketActions(jobId),
  });

  const fire = useMutation({
    mutationFn: (event: string) =>
      api.transition(jobId, event, {
        ...(NEEDS_REASON.has(event) ? { reason: form.reason, holdStatus: form.holdStatus } : {}),
        ...(NEEDS_OUTCOME.has(event)
          ? { outcome: form.outcome, rootCauseCode: form.rootCauseCode || undefined }
          : {}),
        expectedVersion: version,
      }),
    onSuccess: async () => {
      setOpen(null);
      setForm({ reason: '', outcome: 'COMPLETED', rootCauseCode: '', holdStatus: 'ON_HOLD' });
      await queryClient.invalidateQueries();
    },
  });

  const available = (actions.data?.actions ?? []).filter((event) => event in LABELS);
  if (available.length === 0) return null;

  const needsForm = open && (NEEDS_REASON.has(open) || NEEDS_OUTCOME.has(open));

  return (
    <section className="card">
      <h3 className="mb-3 font-heading text-lg">Actions</h3>

      {fire.isError && (
        <div
          role="alert"
          className="mb-3 flex items-start gap-2 rounded-xl bg-danger-50 p-3 text-sm text-danger-600"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{(fire.error as Error).message}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {available.map((event) => (
          <button
            key={event}
            type="button"
            onClick={() => {
              if (NEEDS_REASON.has(event) || NEEDS_OUTCOME.has(event)) {
                setOpen(open === event ? null : event);
              } else {
                fire.mutate(event);
              }
            }}
            disabled={fire.isPending}
            className={
              event === 'cancel'
                ? 'btn-secondary border-danger-500 text-danger-600 hover:bg-danger-50'
                : 'btn-secondary'
            }
          >
            {fire.isPending && fire.variables === event && (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {LABELS[event]}
          </button>
        ))}
      </div>

      {needsForm && (
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          {open === 'hold' && (
            <div>
              <label htmlFor="holdStatus" className="label">
                What are you waiting for?
              </label>
              <select
                id="holdStatus"
                value={form.holdStatus}
                onChange={(e) => setForm({ ...form, holdStatus: e.target.value })}
                className="input-field"
              >
                {HOLD_STATES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {NEEDS_OUTCOME.has(open) && (
            <>
              <div>
                <label htmlFor="outcome" className="label">
                  Outcome
                </label>
                <select
                  id="outcome"
                  value={form.outcome}
                  onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                  className="input-field"
                >
                  {OUTCOMES.map((outcome) => (
                    <option key={outcome} value={outcome}>
                      {outcome.replace(/_/g, ' ').toLowerCase()}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  No access and no fault found are normal outcomes — they are counted for a reason.
                </p>
              </div>

              {form.outcome === 'COMPLETED' && (
                <div>
                  <label htmlFor="rootCause" className="label">
                    Root cause
                  </label>
                  <input
                    id="rootCause"
                    value={form.rootCauseCode}
                    onChange={(e) => setForm({ ...form, rootCauseCode: e.target.value })}
                    placeholder="ONT_FAULTY, DROP_CABLE_CUT, …"
                    className="input-field"
                  />
                </div>
              )}
            </>
          )}

          {NEEDS_REASON.has(open) && (
            <div>
              <label htmlFor="reason" className="label">
                Reason
              </label>
              <input
                id="reason"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Required — it is what makes the timeline defensible"
                className="input-field"
              />
            </div>
          )}

          <button
            type="button"
            onClick={() => fire.mutate(open)}
            disabled={fire.isPending}
            className="btn-primary"
          >
            {fire.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Confirm {LABELS[open]?.toLowerCase()}
          </button>
        </div>
      )}
    </section>
  );
}
